// Self-service avatar editor. Lets any signed-in user pick a themed gallery
// face or cartoon style, upload a photo, or clear back to the default.
// Persists via `PUT /api/me/avatar`. The gallery grid itself is shared with the
// admin user dialog (components/AvatarGalleryPicker.jsx).
import { useEffect, useState } from "react";
import { Upload, X as XIcon, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { cartoonUrlFor } from "@/components/Avatar";
import { AvatarGalleryPicker } from "@/components/AvatarGalleryPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function EditAvatarDialog({ open, onOpenChange, user, onSaved }) {
  const initial = user?.avatar_url || cartoonUrlFor(user?.name || "");
  const [preview, setPreview] = useState(initial);
  const [saving, setSaving] = useState(false);
  // Reset to the stored avatar whenever the dialog (re)opens so an abandoned
  // edit — or a different user — never shows a stale preview.
  useEffect(() => { if (open) setPreview(initial); /* eslint-disable-next-line */ }, [open, user?.user_id]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (file.size > 500 * 1024) { toast.error("Image is over 500KB — please shrink first"); return; }
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/avatar", { avatar_url: preview || "" });
      toast.success(preview ? "Avatar updated" : "Avatar cleared");
      onSaved?.(data.avatar_url || "");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save avatar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-avatar-dialog">
        <DialogHeader>
          <DialogTitle>Your avatar</DialogTitle>
          <DialogDescription>Pick a themed face from a gallery, upload your own photo, or clear to fall back to the default.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-3">
          <div className="h-28 w-28 overflow-hidden rounded-full bg-slate-50 ring-2 ring-slate-200" data-testid="avatar-preview">
            {preview
              ? <img src={preview} alt="Preview" className="h-full w-full object-cover" />
              : <div className="flex h-full w-full items-center justify-center text-3xl font-bold text-slate-300">?</div>}
          </div>

          <AvatarGalleryPicker value={preview} onChange={setPreview}
            seedName={user?.name || "conduit"} testid="avatar" />

          <div className="flex flex-wrap items-center justify-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              data-testid="avatar-upload-label">
              <Upload className="h-3.5 w-3.5" /> Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-upload-input" />
            </label>
            {preview && (
              <Button variant="ghost" size="sm" onClick={() => setPreview("")} data-testid="avatar-clear"
                className="text-slate-500 hover:text-red-500">
                <XIcon className="h-3.5 w-3.5" /> Clear
              </Button>
            )}
          </div>
          <p className="text-center text-[11px] text-slate-400">Photos are stored inline (max 500KB).</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="avatar-save"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
